import React, { useRef, useState } from "react";
import { FocusScope, usePreventScroll } from "react-aria";
import { t } from "ttag";

import { Themed } from "../../styles/themes";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { BurgerButton } from "./BurgerButton";
import { useHeaderContext } from "./HeaderContext";
import { LinkList, normalizeSimpleLinks } from "./LinkList";
import { useSubpanelKeyboardNav } from "./hooks/useSubpanelKeyboardNav";

import styles from "./SearchSubmenu.module.scss";

export const SEARCH_SUBMENU = "header-search-submenu";

export const SearchSubmenu = () => {
    const {
        headerContent,
        isMobileLayout,
        searchUrl,
        activeSubmenu,
        setActiveSubmenu,
    } = useHeaderContext();
    const isOpen = activeSubmenu === SEARCH_SUBMENU;
    const [query, setQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const popularSearches = headerContent.popular_searches.value.map(
        (item) => item.value,
    );
    const hasPopularSearches = popularSearches.length > 0;

    usePreventScroll({ isDisabled: !isOpen });

    const { onKeyDown: panelKeyDown } = useSubpanelKeyboardNav(panelRef);

    if (!isOpen) return null;

    return (
        <FocusScope restoreFocus contain={false}>
            {isMobileLayout && (
                <Themed theme="offWhite" className={styles.closeButtonWrapper}>
                    <BurgerButton
                        active={true}
                        onPress={() => setActiveSubmenu(null)}
                        controls={SEARCH_SUBMENU}
                        label={t`Close search`}
                    />
                </Themed>
            )}
            <Themed
                ref={panelRef}
                theme="offWhite"
                id={SEARCH_SUBMENU}
                role="search"
                className={isMobileLayout ? styles.mobilePanel : styles.panel}
                onKeyDown={panelKeyDown}
            >
                <div className={styles.content}>
                    {hasPopularSearches && (
                        <div className={styles.popularSearches}>
                            <LinkList
                                title={t`Popular Searches`}
                                id="popular-searches"
                                links={normalizeSimpleLinks(popularSearches)}
                            />
                        </div>
                    )}
                    <form
                        className={
                            hasPopularSearches
                                ? styles.searchForm
                                : styles.searchFormFull
                        }
                        method="GET"
                        action={searchUrl}
                        // tabIndex is so we can do our arrow navigation without
                        // getting tripped up on handling inputs
                        tabIndex={0}
                        aria-label={t`Search form`}
                    >
                        <div className={styles.inputRow}>
                            <input
                                ref={inputRef}
                                type="search"
                                name="query"
                                value={query}
                                onChange={(event) =>
                                    setQuery(event.target.value)
                                }
                                placeholder={t`Search`}
                                className={styles.input}
                                aria-label={t`Search`}
                            />
                            {query && (
                                <Clickable
                                    className={styles.clearButton}
                                    onPress={() => {
                                        setQuery("");
                                        inputRef.current?.focus();
                                    }}
                                >
                                    <Svg
                                        name="x"
                                        className={styles.clearIcon}
                                        visuallyHiddenText={t`Clear search`}
                                    />
                                </Clickable>
                            )}
                            <Clickable
                                className={styles.searchButton}
                                type="submit"
                            >
                                <Svg
                                    name="search"
                                    className={styles.searchIcon}
                                    visuallyHiddenText={t`Submit search`}
                                />
                            </Clickable>
                        </div>
                    </form>
                </div>
            </Themed>
        </FocusScope>
    );
};
